/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { VictoryAxis, VictoryChart, VictoryBar, VictoryTooltip } from 'victory';

interface IRetirementChartProps {
  age: number | string;
  retireAge: number;
  requiredContributionPerMonth: number;
  currentContributionPerMonth: number;
  annualIncome: string | number;
}

const Chart = (props: IRetirementChartProps): JSX.Element => {
  const {
    age,
    retireAge,
    requiredContributionPerMonth,
    currentContributionPerMonth,
  } = props;

  // Calculate the number of years remaining
  const yearsRemaining = retireAge - Number(age);
  const isHalfYearly = yearsRemaining < 20;
  const step = isHalfYearly ? 0.4 : 2;
  const adjustedStep = yearsRemaining > 50 ? 5 : yearsRemaining > 30 ? 2 : step; // Dynamic step adjustment

  // Generate Data for Required Contribution (Light Green)
  const requiredData = Array.from(
    { length: Math.floor(yearsRemaining / adjustedStep) },
    (_, i) => {
      const year = (i + 1) * adjustedStep;
      return {
        x: year,
        y: requiredContributionPerMonth * 12 * year,
        label: `€${(
          requiredContributionPerMonth *
          12 *
          year
        ).toLocaleString()}`,
      };
    },
  ).filter((d) => d.x <= yearsRemaining);

  const currentData = Array.from(
    { length: Math.floor(yearsRemaining / adjustedStep) },
    (_, i) => {
      const year = (i + 1) * adjustedStep;
      return {
        x: year,
        y: currentContributionPerMonth * 12 * year,
        label: `€${(currentContributionPerMonth * 12 * year).toLocaleString()}`,
      };
    },
  ).filter((d) => d.x <= yearsRemaining);

  return (
    <VictoryChart>
      {/* Required Contribution Bars (Light Green) */}
      <VictoryBar
        data={requiredData}
        x="x"
        y="y"
        barRatio={0.8} // Adjust bar spacing
        style={{
          data: {
            fill: '#B3C5B4',
            width: 5,
          },
        }}
        labels={({ datum }) => datum.label}
        labelComponent={
          <VictoryTooltip
            flyoutStyle={{
              fill: 'black',
              stroke: 'black',
              borderRadius: 20,
              pointerEvents: 'none',
            }}
            style={{ fill: 'white' }}
            pointerLength={0}
          />
        }
      />

      {/* Current Contribution Bars (Dark Green) */}
      <VictoryBar
        data={currentData}
        x="x"
        y="y"
        barRatio={0.5} // Adjust bar spacing
        style={{
          data: {
            fill: '#8BA78D',
            width: 5,
          },
        }}
        labels={({ datum }) => datum.label}
        labelComponent={
          <VictoryTooltip
            flyoutStyle={{
              fill: 'black',
              stroke: 'black',
              borderRadius: 20,
              pointerEvents: 'none',
            }}
            style={{ fill: 'white' }}
            pointerLength={0}
          />
        }
      />

      {/* X-Axis (Remaining Years) */}
      <VictoryAxis
        tickValues={
          isHalfYearly
            ? requiredData
                .map((d) => Math.round(d.x))
                .filter(
                  (value, index, arr) =>
                    index % Math.ceil(arr.length / 20) === 0 && value !== 0,
                )
            : requiredData.map((d) => d.x)
        }
        tickFormat={(t) => t.toString()} // Show all years
        style={{
          axis: { stroke: '' },
          ticks: { stroke: '#000000' },
          tickLabels: {
            fontSize: 10,
            fill: '#00000098',
          },
        }}
      />
    </VictoryChart>
  );
};

export default Chart;
